import React from 'react';
import clsx from 'clsx';
import { createPortal } from 'react-dom';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import { useIsMobile } from '../../../../../../common/_custom/Hooks/useIsMobile';
import type { EpfEstablishment } from '../../../../../../store/dashboard/compliancesBefe/types';
import { DATE_FORMATS, formatDateByFormat, monthKeyToDisplay, wageMonthToMonthKey } from '../../../../../../utils/dateUtils';
import { capitalizeWords } from '../../../../../../utils/utils';
import {
    EPF_FILING_STATUS_TITLE,
    ESTABLISHMENTS_DRAWER_WIDTH,
    ESTABLISHMENT_FIELD_LABELS as LABELS,
    ESTABLISHMENT_ID_PREFIX,
    LIVE_ESTABLISHMENT_STATUS,
    NO_EPF_PAYMENTS_TEXT,
} from './constants';
import { formatLacCr } from '../../../../../../utils/formatters/formatLacCr';
import { formatEmployees } from './epfFormatters';
import styles from './EpfEstablishmentsDrawer.module.scss';

interface EpfEstablishmentsDrawerProps {
    establishments: EpfEstablishment[];
    onClose: () => void;
}

const formatDisplayDate = (isoDate: string | null): string =>
    formatDateByFormat(isoDate ?? undefined, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY);

const formatWageMonth = (wageMonth: string | null): string => {
    const monthKey = wageMonthToMonthKey(wageMonth);
    return monthKey ? monthKeyToDisplay(monthKey) : wageMonth ?? '';
};

const getLatestWageMonthKey = (establishments: EpfEstablishment[]): string =>
    establishments
        .map((establishment) => wageMonthToMonthKey(establishment.latestWageMonth))
        .filter(Boolean)
        .sort()
        .pop() ?? '';

/** A label/value row that renders nothing when the value is empty — the drawer never prints "null". */
const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) =>
    children === null || children === undefined || children === '' ? null : (
        <div className={styles.Row}>
            <span className={styles.Label}>{label}</span>
            <span className={styles.Value}>{children}</span>
        </div>
    );

const WorkingStatus = ({ status }: { status: string }) => {
    const isLive = status.trim().toUpperCase() === LIVE_ESTABLISHMENT_STATUS;
    return <span className={clsx(styles.StatusPill, { [styles.Live]: isLive })}>{capitalizeWords(status)}</span>;
};

const ExemptionStatus = ({ establishment }: { establishment: EpfEstablishment }) => {
    const lines = [
        [LABELS.EXEMPTION_PF, establishment.exemptionStatusPf],
        [LABELS.EXEMPTION_PENSION, establishment.exemptionStatusPension],
        [LABELS.EXEMPTION_EDLI, establishment.exemptionStatusEdli],
    ].filter(([, value]) => !!value);
    if (!lines.length) return null;
    return (
        <span className={styles.Exemptions}>
            {lines.map(([label, value]) => (
                <span key={label}>
                    <strong>{label}</strong> {value}
                </span>
            ))}
        </span>
    );
};

const EstablishmentCard = ({ establishment }: { establishment: EpfEstablishment }) => (
    <div className={styles.Card} data-testid="epf-establishment-card">
        <div className={styles.CardTitle}>{`${ESTABLISHMENT_ID_PREFIX} ${establishment.establishmentId}`}</div>
        <DetailRow label={LABELS.NAME}>{establishment.establishmentName}</DetailRow>
        <DetailRow label={LABELS.DATE_OF_SETUP}>{formatDisplayDate(establishment.dateOfSetup)}</DetailRow>
        <DetailRow label={LABELS.PRINCIPAL_BUSINESS_ACTIVITIES}>{establishment.principalBusinessActivities}</DetailRow>
        <DetailRow label={LABELS.WORKING_STATUS}>
            {establishment.workingStatus ? <WorkingStatus status={establishment.workingStatus} /> : null}
        </DetailRow>
        <DetailRow label={LABELS.EXEMPTION_STATUS}>
            {establishment.exemptionStatusPf || establishment.exemptionStatusPension || establishment.exemptionStatusEdli ? (
                <ExemptionStatus establishment={establishment} />
            ) : null}
        </DetailRow>
        {establishment.hasPayments ? (
            <>
                <DetailRow label={LABELS.NO_OF_EMPLOYEES}>
                    {establishment.noOfEmployees !== null ? (
                        <span className={styles.Emphasis}>{formatEmployees(establishment.noOfEmployees)}</span>
                    ) : null}
                </DetailRow>
                <DetailRow label={LABELS.LATEST_WAGE_MONTH}>{formatWageMonth(establishment.latestWageMonth)}</DetailRow>
                <DetailRow label={LABELS.LATEST_DATE_OF_CREDIT}>{formatDisplayDate(establishment.latestDateOfCredit)}</DetailRow>
                <DetailRow label={LABELS.LAST_CREDIT}>{establishment.amount !== null ? formatLacCr(establishment.amount) : null}</DetailRow>
            </>
        ) : (
            <div className={styles.NoPayments}>{NO_EPF_PAYMENTS_TEXT}</div>
        )}
    </div>
);

const EpfEstablishmentsDrawer = ({ establishments, onClose }: EpfEstablishmentsDrawerProps) => {
    const isMobile = useIsMobile();
    const latestWageMonthKey = getLatestWageMonthKey(establishments);

    return createPortal(
        <SideDrawer
            heading={
                <span className={styles.Heading}>
                    {EPF_FILING_STATUS_TITLE}
                    {latestWageMonthKey && <span className={styles.HeadingMonth}>{monthKeyToDisplay(latestWageMonthKey)}</span>}
                </span>
            }
            ariaLabel={EPF_FILING_STATUS_TITLE}
            showHeading
            allowClose
            headerVariant="compact"
            width={isMobile ? '100%' : ESTABLISHMENTS_DRAWER_WIDTH}
            onClose={onClose}
        >
            <div className={styles.Body}>
                {establishments.map((establishment) => (
                    <EstablishmentCard key={establishment.establishmentId} establishment={establishment} />
                ))}
            </div>
        </SideDrawer>,
        document.body,
    );
};

export default EpfEstablishmentsDrawer;
